import React, { useEffect, useState } from "react";
import { Loader, Merge, X } from "lucide-react";
import { toast } from "react-toastify";
import { mergeOrders, previewMergeOrders } from "../../api/OrderAPIs";

const money = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "0";
  return Number.isInteger(amount) ? String(amount) : amount.toFixed(2);
};

const field = (preview, camel, pascal) => preview?.[camel] ?? preview?.[pascal];

const MergeOrdersModal = ({ sourceOrder, onClose, onMerged }) => {
  const [otherOrderId, setOtherOrderId] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewForId, setPreviewForId] = useState(null);
  const [previewError, setPreviewError] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setOtherOrderId("");
    setPreview(null);
    setPreviewForId(null);
    setPreviewError("");
  }, [sourceOrder]);

  useEffect(() => {
    if (!sourceOrder) return undefined;

    const otherId = Number(otherOrderId);
    if (!otherId || otherId <= 0 || otherId === Number(sourceOrder.orderNumber)) {
      setPreview(null);
      setPreviewForId(null);
      setPreviewError(otherId && otherId === Number(sourceOrder.orderNumber) ? "لا يمكن دمج الأوردر مع نفسه" : "");
      setPreviewLoading(false);
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setPreviewLoading(true);
      setPreviewError("");
      try {
        const data = await previewMergeOrders(sourceOrder.orderNumber, otherId);
        if (cancelled) return;
        setPreview(data);
        setPreviewForId(otherId);
      } catch (err) {
        if (cancelled) return;
        setPreview(null);
        setPreviewForId(null);
        setPreviewError(err.message || "تعذر حساب العرض");
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [otherOrderId, sourceOrder]);

  if (!sourceOrder) return null;

  const otherId = Number(otherOrderId);
  const previewReady = preview && previewForId === otherId && !previewLoading;
  const offerLabel = field(preview, "offerLabel", "OfferLabel");
  const freeQuantity = Number(field(preview, "freeQuantity", "FreeQuantity")) || 0;
  const eligibleQuantity = Number(field(preview, "eligibleQuantity", "EligibleQuantity")) || 0;
  const totalAmount = field(preview, "totalAmount", "TotalAmount");
  const notice = field(preview, "notice", "Notice");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving || !previewReady) return;

    if (!otherId || otherId <= 0) {
      toast.error("حط رقم الأوردر التاني اللي هتدمجه");
      return;
    }

    const offerText = offerLabel
      ? `${offerLabel} — ${freeQuantity} قطع من الأرخص مجانًا`
      : "مفيش قطع مجانية على العدد ده";
    const confirmed = window.confirm(
      `تأكيد دمج الأوردر #${otherId} مع #${sourceOrder.orderNumber}؟\n${offerText}\nالإجمالي بعد العرض: ${money(totalAmount)} ج.م\nالأوردر #${otherId} هيتحول لقديم ولن يُحذف.`
    );
    if (!confirmed) return;

    setSaving(true);
    try {
      const updated = await mergeOrders(sourceOrder.orderNumber, otherId);
      const savedTotal = updated?.totalAmount ?? updated?.TotalAmount ?? totalAmount;
      toast.success(`تم الدمج وتطبيق العرض. الإجمالي ${money(savedTotal)} ج.م`);
      onMerged?.(updated, otherId);
      onClose();
    } catch (err) {
      toast.error(err.message || "فشل دمج الطلبات");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4"
      dir="rtl"
      onClick={(e) => e.target === e.currentTarget && !saving && onClose()}
    >
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b bg-gradient-to-l from-teal-50 to-white">
          <div className="flex items-center gap-2">
            <Merge className="text-teal-700" size={22} />
            <div>
              <h2 className="text-lg font-bold text-teal-800">دمج الطلبات</h2>
              <p className="text-sm text-gray-500">الأوردر الأصلي #{sourceOrder.orderNumber}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="p-2 rounded-xl hover:bg-gray-100 disabled:opacity-50"
          >
            <X size={22} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <label className="block">
            <span className="block mb-1 font-medium text-gray-700">رقم الأوردر التاني</span>
            <input
              type="number"
              min="1"
              required
              autoFocus
              value={otherOrderId}
              onChange={(e) => setOtherOrderId(e.target.value)}
              className="w-full border-2 border-gray-200 rounded-xl px-3 py-3 focus:outline-none focus:ring-4 focus:ring-teal-200 focus:border-teal-400"
              placeholder="حط رقم الأوردر اللي هيتدمج"
            />
          </label>

          {previewLoading && (
            <p className="text-sm text-teal-700 flex items-center gap-2">
              <Loader className="animate-spin" size={16} />
              بنحسب العرض على القطع بعد الدمج...
            </p>
          )}

          {previewError && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2">
              {previewError}
            </p>
          )}

          {previewReady && (
            <div className="rounded-xl border border-teal-100 bg-teal-50/70 px-3 py-3 space-y-1 text-sm text-teal-950">
              <p className="font-bold">
                {offerLabel || "مفيش قطع مجانية على العدد ده"}
              </p>
              {freeQuantity > 0 && (
                <p>{freeQuantity} من {eligibleQuantity} قطع هتبقى مجانية، والأرخص هو اللي بيتخصم.</p>
              )}
              <p>المنتجات: {money(field(preview, "productsSubtotal", "ProductsSubtotal"))} ج.م</p>
              <p>خصم العرض: {money(field(preview, "offerDiscount", "OfferDiscount"))} ج.م</p>
              <p>الشحن: {money(field(preview, "shippingAmount", "ShippingAmount"))} ج.م</p>
              {Number(field(preview, "couponDiscountAmount", "CouponDiscountAmount")) > 0 && (
                <p>الكوبون: {money(field(preview, "couponDiscountAmount", "CouponDiscountAmount"))} ج.م</p>
              )}
              <p className="text-lg font-bold pt-1">الإجمالي: {money(totalAmount)} ج.م</p>
              {notice && <p className="text-amber-800">{notice}</p>}
            </div>
          )}

          <p className="text-sm text-gray-500 leading-relaxed">
            العرض الحالي للموقع بيتطبق لوحده على القطع كلها بعد الدمج، زي العربة. الشحن بيتفضل شحن الأوردر الأصلي، والأوردر التاني هيتحول لقديم.
          </p>
        </div>

        <div className="flex gap-3 px-5 py-4 border-t bg-gray-50">
          <button
            type="submit"
            disabled={saving || !previewReady}
            className="flex-1 bg-teal-600 hover:bg-teal-700 text-white px-4 py-3 rounded-xl font-bold disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving ? (
              <>
                <Loader className="animate-spin" size={18} />
                جاري الدمج...
              </>
            ) : (
              <>
                <Merge size={18} />
                تأكيد الدمج
              </>
            )}
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-3 rounded-xl border-2 border-gray-200 font-medium hover:bg-white disabled:opacity-50"
          >
            إلغاء
          </button>
        </div>
      </form>
    </div>
  );
};

export default MergeOrdersModal;
